import { useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { pickerInitials, type PickerCardItem } from '@/scheduling/lib/picker-cards';

/**
 * PickerGrid — the 2-column "search over cards" control, with NOTHING in it
 * that knows what an item is.
 *
 * It was `ProviderPickerGrid`'s body until PROD-2193. Andrew, 2026-09-13:
 * "the device dropdown should also be searchable". The Book-a-device rail
 * needed the same control the practitioner rail already has, and this
 * practice has 57 devices — but the provider picker is bound to
 * `ProviderSlotsResponse` (a speciality object, a `workingHours` array, the
 * `Dr.` prefix rule), so a device could only have gone through it as a faked
 * provider. Rather than clone the card markup into a device copy that would
 * drift, the markup moved DOWN here and both pickers became thin adapters
 * over it:
 *
 *   ProviderPickerGrid  ─┐
 *                        ├─→ PickerGrid  (search · cards · collapse · selected)
 *   DevicePickerGrid    ─┘
 *
 * So polish to the card, the search box, the empty state or the chevron lands
 * once and reaches both surfaces. The rendered DOM and class names are
 * verbatim what the provider picker rendered before the extraction — this is
 * a refactor of where the markup lives, not of what it looks like.
 *
 * Presentational only: no form context, no query, no selection state. The
 * caller owns filtering (pass the already-narrowed list) and selection.
 */

interface PickerGridProps {
  /** The full list to render — the caller narrows it before passing it in. */
  items: PickerCardItem[];
  /** The selected card, which REPLACES the grid. */
  selected: PickerCardItem | null;
  isLoading: boolean;
  onSelect: (item: PickerCardItem, index: number) => void;
  onClear: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Hide the search input entirely. Default false. */
  hideSearch?: boolean;
  /** Cards shown before the chevron clamps the list. `Infinity` = no clamp. */
  collapsedCount?: number;
  /** Extra classes on the card grid — e.g. a max height + scroll for a long list. */
  listClassName?: string;
  emptyText?: string;
  noMatchText?: string;
  /** Accessible name of the × on the selected card. */
  clearLabel?: string;
  /** Accessible name of the expand chevron in its collapsed state. */
  expandLabel?: string;
  /** View-only: no ×, no search, cards not clickable. */
  readOnly?: boolean;
}

export function PickerGrid({
  items,
  selected,
  isLoading,
  onSelect,
  onClear,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search',
  hideSearch = false,
  collapsedCount = 4,
  listClassName,
  emptyText = 'Nothing available.',
  noMatchText = 'Nothing matches this search.',
  clearLabel = 'Clear selection',
  expandLabel = 'Show more',
  readOnly = false,
}: PickerGridProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleItems = isExpanded ? items : items.slice(0, collapsedCount);
  const canExpand = items.length > collapsedCount;

  if (selected) {
    return (
      <div className="flex items-center gap-3 p-2 rounded-lg border border-brand-primary bg-blue-100">
        <Avatar className="h-8 w-8 flex-shrink-0">
          {selected.photo ? <AvatarImage src={selected.photo} alt="" /> : null}
          <AvatarFallback className="text-size-xs font-semibold bg-blue-100 text-brand-depth">
            {selected.initials ?? pickerInitials(selected.title)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate">{selected.title}</p>
          {selected.subtitle && (
            <p className="text-xs text-text-muted truncate">{selected.subtitle}</p>
          )}
          {selected.meta && (
            <p className="text-xs text-brand-primary truncate">{selected.meta}</p>
          )}
        </div>
        {!readOnly && (
          <button
            type="button"
            aria-label={clearLabel}
            onClick={onClear}
            className="p-1 hover:bg-grey-100 rounded"
          >
            <X className="h-4 w-4 text-text-muted" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {!hideSearch && (
        <div className="relative">
          <Search className="absolute left-3 top-2 h-4 w-4 text-text-hint pointer-events-none" />
          <input
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full h-8 pl-8 pr-3 rounded-lg border border-border-default bg-surface-card text-sm text-text-primary focus:border-brand-primary focus:outline-none"
          />
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-grey-200 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-text-muted py-2">{searchValue ? noMatchText : emptyText}</p>
      ) : (
        <>
          <div className={cn('grid grid-cols-2 gap-2', listClassName)}>
            {visibleItems.map((item, i) => (
              <button
                key={item.id || `picker-item-${i}`}
                type="button"
                onClick={() => onSelect(item, i)}
                className="flex items-center gap-3 p-3 rounded-xl border border-border-default bg-surface-card text-left transition-colors hover:border-brand-primary hover:bg-blue-100"
              >
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <AvatarImage src={item.photo ?? undefined} />
                  <AvatarFallback className="text-size-xs font-semibold bg-blue-100 text-brand-depth">
                    {item.initials ?? pickerInitials(item.title)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-primary truncate leading-snug">
                    {item.title}
                  </p>
                  {item.subtitle && (
                    <p className="text-xs text-text-secondary truncate leading-snug">
                      {item.subtitle}
                    </p>
                  )}
                  {item.meta && (
                    <p className="text-xs text-brand-primary truncate leading-snug">{item.meta}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
          {canExpand && (
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="mt-2 mx-auto flex items-center justify-center w-8 h-8 rounded-full text-brand-primary hover:bg-blue-100"
              aria-label={isExpanded ? 'Show less' : expandLabel}
              aria-expanded={isExpanded}
            >
              <ChevronDown
                className={cn('h-5 w-5 transition-transform', isExpanded && 'rotate-180')}
              />
            </button>
          )}
        </>
      )}
    </div>
  );
}
